import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { cancelShipment, repostShipment } from '../../api/endpoints/admin';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { useMutationHook } from '../../hooks/useMutationHook';
import { actionFailureMessage } from '../../api/actionFailure';
import styles from './BiddingClosedPanel.module.css';

const INVALIDATE_KEYS = [
  ['admin', 'scorecard', 'me'],
  ['admin', 'work'],
];
const navState = { from: '/work', fromLabel: 'My Work' };

/** Team-wide POSTED loads whose bidding closed with no award (ids come from the live scorecard). */
export function BiddingClosedPanel({ shipmentIds }: { shipmentIds: number[] }) {
  const { t } = useTranslation();
  return (
    <section className={styles.panel} aria-label={t('work.biddingClosedPanel.label')}>
      <h2 className={styles.title}>{t('work.biddingClosedPanel.title', { n: shipmentIds.length })}</h2>
      {shipmentIds.length === 0 ? (
        <p className={styles.empty}>{t('work.biddingClosedPanel.empty')}</p>
      ) : (
        <ul className={styles.list}>
          {shipmentIds.map((id) => (
            <BiddingClosedRow key={id} id={id} />
          ))}
        </ul>
      )}
    </section>
  );
}

function BiddingClosedRow({ id }: { id: number }) {
  const { t } = useTranslation();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);

  const repostMut = useMutationHook(() => repostShipment(id), {
    successMessage: 'Load reposted. Bidding is open again.',
    errorMessage: "Couldn't repost the load. Try again.",
    invalidateKeys: INVALIDATE_KEYS,
  });
  const cancelMut = useMutationHook(() => cancelShipment(id), {
    successMessage: 'Load cancelled.',
    errorMessage: actionFailureMessage,
    invalidateKeys: INVALIDATE_KEYS,
    onSuccess: () => setConfirmOpen(false),
    onError: (err) => setDialogError(actionFailureMessage(err)),
  });
  const busy = repostMut.isLoading || cancelMut.isLoading;

  return (
    <li className={styles.row} data-testid={`bidding-closed-row-${id}`}>
      <Link to={`/shipments/${id}`} state={navState} className={styles.idLink}>
        #{id}
      </Link>
      <span className={styles.reason}>
        {t('work.biddingClosedPanel.reason')}
      </span>
      <span className={styles.actions}>
        <button
          type="button"
          className={styles.repostBtn}
          disabled={busy}
          onClick={() => repostMut.mutate(undefined)}
        >
          {repostMut.isLoading ? 'Reposting…' : 'Repost'}
        </button>
        <button
          type="button"
          className={styles.cancelBtn}
          disabled={busy}
          onClick={() => setConfirmOpen(true)}
        >
          {t('common.actions.cancel')}
        </button>
      </span>
      <ConfirmDialog
        isOpen={confirmOpen}
        title={`Cancel load #${id}?`}
        description="This cancels the load. It can't be undone."
        confirmText="Cancel load"
        cancelText="Keep load"
        variant="danger"
        help="cancel-load"
        loading={cancelMut.isLoading}
        errorMessage={dialogError}
        onConfirm={() => {
          setDialogError(null);
          cancelMut.mutate(undefined);
        }}
        onCancel={() => {
          setDialogError(null);
          setConfirmOpen(false);
        }}
      />
    </li>
  );
}
